import { CategoryId, epgCategoriesModel } from '$/epg/categories'
import { PATH, SEGMENT, type ReservedNameType } from '@/constants'
import { createEvent, createStore, sample } from 'effector'
import { model as router } from '~/core/router'
import { parseEpgPath, updateEpgPath } from '~/entities/epg/lib'
import { loadEpgCategoriesModel } from '~/features/epg/categories/load'
import { lookbehind } from '~/shared/effector'
import { formatTitleForUrl, getCategoryIdFromSegment } from '~/shared/helpers'

export const autoSetActiveId = createEvent<{
  [SEGMENT.CATEGORY]?: string
}>()
export const setActiveId = createEvent<number | CategoryId | null>()
export const resetActiveId = createEvent()

export const $activeId = createStore<number | CategoryId | null>(null)
  .reset(resetActiveId)
  .on(setActiveId, (_, id) => id)
  .on(autoSetActiveId, (current, params) => {
    // hack to set All category if no category segment in url (used for cases when user click browser back button)
    if (params[SEGMENT.CATEGORY] === undefined) return CategoryId.All

    return getCategoryIdFromSegment(params[SEGMENT.CATEGORY]) ?? current
  })
//
// persist active category id to url
//

const pushActiveIdToUrl = createEvent<number | ReservedNameType | null>()

lookbehind({
  source: $activeId,
  clear: autoSetActiveId,
  within: 0,
  target: pushActiveIdToUrl,
})

// EPG page navigations:
// - make seo friendly url: /region-title-1/10 -> /region-title-1/category-title-1
// - correct title: /region-title-1/sp-10 -> /region-title-1/sports-1
// - navigate to 'All' if invalid category segment: /region-title-1/invalid-category-segment -> /region-title-1/all
// (also can be another combinations)
sample({
  clock: [autoSetActiveId, router.$pathname, epgCategoriesModel.$allCategories],
  source: {
    pathname: router.$pathname,
    activeId: $activeId,
    categories: epgCategoriesModel.$allCategories,
  },
  filter: ({ pathname, activeId, categories }) => {
    const { categoryId: categoryIdUrl, categoryTitle: categoryTitleUrl } =
      parseEpgPath(pathname ?? '')
    const category = categories?.find((c) => c.id === activeId)

    // Need to navigate to All if invalid category segment
    if (activeId === null && categoryIdUrl === null && categoryTitleUrl?.length)
      return true
    // if no title no need to update url
    if (!activeId || activeId !== category?.id || !category?.title) return false

    // Check url category
    const { categoryId, categoryTitle } = parseEpgPath(pathname ?? '')
    if (!categoryId || categoryId !== activeId) return false

    const categoryFoundedId = category?.id

    const existedCategorySegment =
      categoryId === CategoryId.All || categoryId === CategoryId.Favorite
        ? categoryId
        : `${categoryTitle}-${categoryId}`

    const correctCategorySegment =
      categoryFoundedId === CategoryId.All ||
      categoryFoundedId === CategoryId.Favorite
        ? categoryFoundedId
        : `${formatTitleForUrl(category?.title)}${categoryFoundedId}`

    return (
      existedCategorySegment !== correctCategorySegment &&
      Boolean(pathname?.startsWith(`${PATH.LIVE}${PATH.CATEGORIES}`))
    )
  },
  fn: ({ pathname, activeId, categories }) => {
    // Navigate to All if invalid category segment
    if (activeId === null)
      return updateEpgPath(pathname, {
        categoryId: CategoryId.All,
        categoryTitle: '',
      })

    const category = categories?.find((c) => c.id === activeId)

    return updateEpgPath(pathname, {
      categoryId: category?.id ?? activeId,
      categoryTitle: category?.title ?? '',
    })
  },
  target: router.navigatePush,
})

sample({
  clock: pushActiveIdToUrl,
  source: {
    pathname: router.$pathname,
    allCategories: epgCategoriesModel.$allCategories,
  },
  // EPG-board page
  filter: ({ pathname }, id) =>
    // prevent pushing url to history if id is null to prevent double navigation when user click browser back button
    Boolean(pathname?.startsWith(`${PATH.LIVE}${PATH.CATEGORIES}`)) &&
    Boolean(id),
  fn: ({ pathname, allCategories }, id) => {
    const category = allCategories.find((category) => category.id === id)
    return updateEpgPath(pathname, {
      categoryId: id,
      categoryTitle: category?.title,
    })
  },
  target: router.navigatePush,
})

sample({
  clock: pushActiveIdToUrl,
  source: {
    pathname: router.$pathname,
    allCategories: epgCategoriesModel.$allCategories,
  },
  // EPG-board page
  filter: ({ pathname }, id) =>
    // if id is null, replace url with All category without pushing to history
    Boolean(pathname?.startsWith(`${PATH.LIVE}${PATH.CATEGORIES}`)) && !id,
  fn: ({ pathname, allCategories }, id) => {
    const category = allCategories.find((category) => category.id === id)
    return updateEpgPath(pathname, {
      categoryId: id,
      categoryTitle: category?.title,
    })
  },
  target: router.navigateReplace,
})

//
//
//

// select first category if there is no active category yet
sample({
  clock: autoSetActiveId,
  source: {
    activeId: $activeId,
    madeup: epgCategoriesModel.$madeup,
    loaded: epgCategoriesModel.$loaded,
  },
  filter: ({ activeId, madeup, loaded }) => {
    const hasCategories = madeup.length + loaded.length > 0

    // if there are categories and there is no active category id set in url
    return hasCategories && activeId == null
  },

  fn: ({ madeup, loaded }) => {
    const allCategory = madeup.find(({ id }) => id === CategoryId.All)
    return allCategory?.id ?? madeup[0]?.id ?? loaded[0]?.id ?? null
  },
  target: setActiveId,
})

// fallback to All when active category id is not found in the final category list
sample({
  clock: [epgCategoriesModel.$allCategories],
  source: {
    activeId: $activeId,
    allCategories: epgCategoriesModel.$allCategories,
    ready: loadEpgCategoriesModel.$ready,
    pathname: router.$pathname,
  },
  filter: ({ activeId, allCategories, ready, pathname }) => {
    if (!ready || activeId == null) return false
    if (!pathname?.startsWith(`${PATH.LIVE}${PATH.CATEGORIES}`)) return false

    const categoryExists = allCategories.some(({ id }) => id === activeId)
    return !categoryExists
  },
  fn: () => CategoryId.All,
  target: [setActiveId, pushActiveIdToUrl],
})
